// Lightweight audit logger. Resolves the acting user once per session
// (cached in-memory) and inserts an audit_log row.
import { supabase } from "@/integrations/supabase/client";

let cached: { name: string; role: string; nursery_id: string | null } | null = null;

async function getActor() {
  if (cached) return cached;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { name: "System", role: "system", nursery_id: null };
  const [{ data: p }, { data: rows }] = await Promise.all([
    supabase.from("profiles").select("first_name,last_name,nursery_id").eq("user_id", user.id).maybeSingle(),
    supabase.from("user_roles").select("role").eq("user_id", user.id),
  ]);
  const roles = ((rows as Array<{ role: string }> | null) || []).map((r) => r.role);
  const role = roles.includes("super_admin")
    ? "super_admin"
    : roles.includes("admin")
      ? "admin"
      : roles.includes("editor")
        ? "editor"
        : "viewer";
  const name = `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || user.email || "User";
  cached = { name, role, nursery_id: (p?.nursery_id as string | null) ?? null };
  return cached;
}

export function resetAuditActorCache() { cached = null; }

export interface AuditEntry {
  description: string;
  recordType?: string;
  recordId?: string | null;
  status?: string;
}

export async function logAudit(entry: AuditEntry): Promise<void> {
  try {
    const actor = await getActor();
    if (!actor.nursery_id) return;
    await supabase.from("audit_log").insert({
      description: entry.description,
      record_type: entry.recordType || "",
      record_id: entry.recordId || null,
      status: entry.status || "Approved",
      user_name: actor.name,
      user_role: actor.role,
      nursery_id: actor.nursery_id,
    } as any);
  } catch (e) {
    console.warn("logAudit failed", e);
  }
}
